import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { ANALYTICS_EVENTS } from "../../../analytics-store.tsx";
import { APPS } from "../../../constants/AppsConstants.ts";
import { ImageEditingFeatureDetector } from "../../../constants/ImageEditingFeatures";
import {
	CONTENT_MODERATION_INPUT_ERROR,
	PollingFileStatus,
	engineExpansionError,
} from "../../../constants/ImageToImageConstants.ts";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { IRootStore } from "../../../mobx/root-store.tsx";
import {
	ImageEditingViewMode,
	ImageToImageConfigType,
	OfflineProps,
	PlaygroundImage,
	PlaygroundResult,
} from "../../../models/image-to-image.ts";
import { Task } from "../../../models/playground.ts";
import postHogService from "../../../services/PostHogService";
import QueryService from "../../../utils/QueryService.ts";
import { isFoxApps } from "../../../utils/index.ts";
import useImageUtils from "../../../utils/useImageUtils.tsx";
import iframeStore from "../../IframeNew/iframe-store.tsx";
import { IImageToImageStore } from "./image-to-image-store.tsx";

export interface IImageToImageGenerationStore {
	isGeneratingImages: boolean;
	abortController: AbortController;
	lastSelectedImages: PlaygroundImage[];

	generateImageToImage: (
		images?: PlaygroundImage[],
		config?: ImageToImageConfigType,
		isFromUpload?: boolean,
		numResults?: number,
		skipTracking?: boolean,
	) => Promise<void>;
	abortImageGeneration: () => void;
}

export class ImageToImageGenerationStore implements IImageToImageGenerationStore {
	rootStore: IRootStore;
	imageToImageStore: IImageToImageStore;
	private queryService: QueryService = new QueryService("/image-to-image");

	// ===== LOADING STATES =====
	isGeneratingImages = false;

	// ===== ABORT CONTROL =====
	abortController: AbortController = new AbortController();
	lastSelectedImages: PlaygroundImage[] = [];

	constructor(rootStore: IRootStore, imageToImageStore: IImageToImageStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
		this.imageToImageStore = imageToImageStore;
	}

	private initializeGenerationContext(
		images: PlaygroundImage[] | undefined,
		config: ImageToImageConfigType,
	): {
		selectedImages: PlaygroundImage[];
		startTime: number;
		isBulkMode: boolean;
		featureString: string;
	} {
		const { playgroundStore } = this.rootStore;
		this.abortController = new AbortController();
		const selectedImages: PlaygroundImage[] = images || playgroundStore.getSelectedImages();
		this.lastSelectedImages = selectedImages;
		const startTime = Date.now();
		const isBulkMode = playgroundStore.imageEditingViewMode !== ImageEditingViewMode.SINGLE;
		const featureString = ImageEditingFeatureDetector.getFeatureString(config, isBulkMode);

		return { selectedImages, startTime, isBulkMode, featureString };
	}

	private async trackGenerationStarted(
		featureString: string,
		isBulkMode: boolean,
		imageCount: number,
		skipTracking?: boolean,
	): Promise<void> {
		if (!skipTracking) {
			await postHogService.trackEvent(
				POSTHOG_EVENTS.IMAGE_EDITING_STARTED,
				{
					feature: featureString,
					mode: isBulkMode ? ImageEditingViewMode.GALLERY : ImageEditingViewMode.SINGLE,
					image_count: imageCount,
				},
				{ send_instantly: true },
			);
		}
	}

	private getExistingVariationsCount(selectedImage: PlaygroundImage): number {
		const { playgroundStore } = this.rootStore;
		return playgroundStore.playgroundResults.flatMap((result) =>
			result.images.filter(
				(img) =>
					img.type === "imageToImage" &&
					(img.config as ImageToImageConfigType).original_image.id === selectedImage.id,
			),
		).length;
	}

	private createPrepareRequestFormData(
		selectedImage: PlaygroundImage,
		config: ImageToImageConfigType,
		existingVariations: number,
	): FormData {
		const { playgroundStore } = this.rootStore;
		const formData = new FormData();
		if (selectedImage.file) {
			formData.append("file", selectedImage.file);
		}

		formData.append(
			"config",
			JSON.stringify({
				...config,
				original_image: {
					...selectedImage,
					offline_props: {
						...selectedImage.offline_props,
						variation: existingVariations,
					},
					from_app: playgroundStore.selectedConfig,
				},
			}),
		);

		return formData;
	}

	private async prepareGenerationTasks(
		formData: FormData,
	): Promise<{ original: boolean; process_style: boolean; tasks: Task[] }> {
		const prepareRes = await this.queryService.post(
			"/prepare_image_generations_tasks",
			formData,
			{ "Content-Type": "multipart/form-data" },
			{ signal: this.abortController.signal },
		);
		return {
			original: prepareRes.original,
			process_style: prepareRes.process_style,
			tasks: prepareRes.tasks,
		};
	}

	private createGenerateRequestFormData(
		selectedImage: PlaygroundImage,
		config: ImageToImageConfigType,
		existingVariations: number,
		configOverrides: Partial<{
			original: boolean;
			tasks: Task[];
			process_style: boolean;
		}>,
	): FormData {
		const { playgroundStore } = this.rootStore;
		const generateFormData = new FormData();
		if (selectedImage.file) {
			generateFormData.append("file", selectedImage.file);
		}

		generateFormData.append(
			"config",
			JSON.stringify({
				...config,
				original_image: {
					...selectedImage,
					offline_props: {
						...selectedImage.offline_props,
						variation: existingVariations,
					},
					from_app: playgroundStore.selectedConfig,
				},
				...configOverrides,
			}),
		);

		return generateFormData;
	}

	private async runGenerateAPI(
		selectedImage: PlaygroundImage,
		config: ImageToImageConfigType,
		existingVariations: number,
		configOverrides: Partial<{
			original: boolean;
			tasks: Task[];
			process_style: boolean;
		}>,
	): Promise<{ results: PlaygroundImage[]; original_url: string }> {
		const generateFormData = this.createGenerateRequestFormData(
			selectedImage,
			config,
			existingVariations,
			configOverrides,
		);

		const {
			results,
			original_url,
		}: {
			results: PlaygroundImage[];
			original_url: string;
		} = await this.queryService.post(
			"/",
			generateFormData,
			{ "Content-Type": "multipart/form-data" },
			{ signal: this.abortController.signal },
		);

		return { results, original_url };
	}

	private validateResultsForContentModeration(results: PlaygroundImage[]): void {
		for (let i = 0; i < results.length; i++) {
			const generatedImage = results[i];
			if (generatedImage.error?.status === 422) {
				runInAction(() => {
					this.imageToImageStore.errors.generate = { message: "content moderation", status: 422 };
				});
				throw new Error(CONTENT_MODERATION_INPUT_ERROR);
			}
		}
	}

	private createPlaygroundImageFromResult(
		generatedImage: PlaygroundImage,
		selectedImage: PlaygroundImage,
		originalUrl: string,
		existingVariations: number,
		resultIndex: number,
		isFromUpload: boolean,
		savedIndex: number,
		targetImageId: string,
		errorMessage?: string,
		task?: Task,
	): PlaygroundImage {
		const { playgroundStore } = this.rootStore;
		const newImage = {
			...generatedImage,
			id: isFromUpload ? uuidv4() : targetImageId,
			...(!isFoxApps() && !isFromUpload && selectedImage?.file && { file: selectedImage.file }),
			loading: errorMessage ? false : true,
			config: {
				...playgroundStore.playgroundResults[savedIndex].config,
				...(!isFromUpload && { original_image: { ...selectedImage, url: originalUrl } }),
			} as ImageToImageConfigType,
			image_config: task?.image_config ?? null,
			mode: playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE ? "single" : "bulk",
			type: isFromUpload ? "upload" : playgroundStore.selectedConfig,
			sessionId: (selectedImage.sessionId ?? originalUrl).split("?")[0],
			variationNum: existingVariations + resultIndex + 1,
			offline_props: {
				...(selectedImage.offline_props as OfflineProps),
				variation: existingVariations + resultIndex + 1,
			},
			playground_result_id: undefined,
		};

		if (errorMessage) {
			(newImage as any).errorType =
				errorMessage === PollingFileStatus.ZERO_BYTE_IMAGE_ERROR
					? PollingFileStatus.CONTENT_MODERATION
					: PollingFileStatus.UNAVAILABLE;
		}

		return newImage as PlaygroundImage;
	}

	private async updatePlaygroundResultWithAutomotiveImages(
		savedIndex: number,
		finalResults: Array<{ results: PlaygroundImage[]; original_url: string; task?: Task }>,
	): Promise<void> {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		const { pullUntilAvailable } = useImageUtils();

		if (playgroundStore.selectedConfig !== APPS.AUTOMOTIVE) {
			return;
		}

		try {
			const images = playgroundStore.playgroundResults[savedIndex].images;
			const playground_result: PlaygroundResult | undefined = await projectsStore.createPlaygroundResult({
				...playgroundStore.playgroundResults[savedIndex],
				images: [],
			});

			if (playground_result) {
				await Promise.allSettled(
					images.map(async (img, imageIndex) => {
						try {
							await pullUntilAvailable(img.url, {
								abortController: this.abortController,
							});

							const updatedResult = await projectsStore.uploadImageToPlaygroundResult({
								...img,
								playground_result_id: playground_result.id,
								sessionId: playground_result.session_id,
							});

							if (updatedResult) {
								runInAction(() => {
									const existingIndex = playgroundStore.playgroundResults[
										savedIndex
									].images.findIndex((i) => i.id === img.id || (i.url === "" && i.loading));

									if (existingIndex !== -1) {
										playgroundStore.playgroundResults[savedIndex].images[existingIndex] = {
											...updatedResult,
											loading: false,
											image_config: finalResults[0]?.task?.image_config ?? null,
											mode:
												playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE
													? "single"
													: "bulk",
										};
									}
								});
							}
						} catch (error) {
							console.warn(`Image ${imageIndex} failed to load or upload:`, error);
						}
					}),
				);
			}
		} catch (error) {
			console.error("Failed to create playground result:", error);
		}
	}

	private updateStoreWithResults(
		finalResults: Array<{ results: PlaygroundImage[]; original_url: string; task?: Task }>,
		selectedImage: PlaygroundImage,
		config: ImageToImageConfigType,
		existingVariations: number,
		savedIndex: number,
		imageIndex: number,
		isFromUpload: boolean,
		resultImages: PlaygroundImage[],
	): void {
		const { playgroundStore } = this.rootStore;
		const flattenedResults = finalResults.flatMap((item) => item.results);
		const errorMessage = (flattenedResults?.[0] as any)?.error?.message;
		const originalUrl: string = finalResults.length > 0 ? finalResults[0].original_url : "";

		if (finalResults.length === 0) {
			this.rootStore.playgroundStore.removeNotLoadingImages();
			return;
		}

		this.validateResultsForContentModeration(flattenedResults);
		resultImages.push(...flattenedResults);

		runInAction(async () => {
			const startIndex = imageIndex * config.num_results;

			flattenedResults.forEach((generatedImage: PlaygroundImage, resultIndex: number) => {
				const finalIndex = startIndex + resultIndex;
				const availableIndex = playgroundStore.playgroundResults[savedIndex].images.findIndex(
					(img) => img.loading && img.url === "",
				);
				const targetIndex = availableIndex !== -1 ? availableIndex : finalIndex;

				// Get the target image ID (for non-upload flows, preserve skeleton ID)
				const targetImageId = isFromUpload
					? uuidv4()
					: playgroundStore.playgroundResults[savedIndex].images[targetIndex]?.id || uuidv4();

				const newImage = this.createPlaygroundImageFromResult(
					generatedImage,
					selectedImage,
					originalUrl,
					existingVariations,
					resultIndex,
					isFromUpload,
					savedIndex,
					targetImageId,
					errorMessage,
					finalResults[0]?.task,
				);

				playgroundStore.playgroundResults[savedIndex].images[targetIndex] = newImage;
				playgroundStore.handleSelectSingleImage(newImage);

				if (isFoxApps()) {
					if (isFromUpload) {
						this.rootStore.analyticsStore.logImageToImageEvent(
							ANALYTICS_EVENTS.IMAGE_UPLOAD_ITI,
							playgroundStore.playgroundResults[savedIndex].images[targetIndex],
						);
					} else {
						this.rootStore.analyticsStore.logImageToImageEvent(
							ANALYTICS_EVENTS.ITI_GENERATE,
							playgroundStore.playgroundResults[savedIndex].images[targetIndex],
						);
					}
				}
			});

			await this.updatePlaygroundResultWithAutomotiveImages(savedIndex, finalResults);
		});
	}

	private removeFailedSkeleton(
		taskIndex: number,
		selectedImage: PlaygroundImage,
		imageIndex: number,
		tasksLength: number,
		deletedFlags: boolean[],
		savedIndex: number,
	): void {
		const { playgroundStore } = this.rootStore;
		const originalIndex = imageIndex * tasksLength + taskIndex;
		const priorDeletions = deletedFlags.slice(0, originalIndex).filter(Boolean).length;
		const adjustedIndex = originalIndex - priorDeletions;
		playgroundStore.playgroundResults[savedIndex]?.images?.splice(adjustedIndex, 1);
		deletedFlags[originalIndex] = true;
		playgroundStore.handleSelectSingleImage(selectedImage);
	}

	private handleTaskError(
		error: any,
		taskIndex: number,
		selectedImage: PlaygroundImage,
		selectedImages: PlaygroundImage[],
		imageIndex: number,
		tasksLength: number,
		deletedFlags: boolean[],
		savedIndex: number,
	): void {
		if (error.response && error.response.status === 420) {
			// Note: 420 errors are handled separately in processGenerationTasks with fakeResults
			return;
		}

		if (
			error?.message === CONTENT_MODERATION_INPUT_ERROR ||
			((error.response?.status === 422 || error.response?.status === 400) &&
				error.response.data?.message === engineExpansionError)
		) {
			const isContentModerationError = error.message === CONTENT_MODERATION_INPUT_ERROR;
			const isExpansionError = !isContentModerationError;

			if (isContentModerationError && selectedImages.length === 1) {
				runInAction(() => {
					// Access thumbnailNavigationSlideToImage via container store (errorStore will be created in Phase 6)
					(this.imageToImageStore as any).thumbnailNavigationSlideToImage = selectedImage;
				});
				throw new Error("Image generation failed with status 422 and empty URL");
			}

			if (isExpansionError) {
				runInAction(() => {
					// Access thumbnailNavigationSlideToImage via container store (errorStore will be created in Phase 6)
					(this.imageToImageStore as any).thumbnailNavigationSlideToImage = selectedImage;
					this.removeFailedSkeleton(
						taskIndex,
						selectedImage,
						imageIndex,
						tasksLength,
						deletedFlags,
						savedIndex,
					);
				});
			}
		} else if (error.response && (error.response.status === 415 || error.response.status === 460)) {
			runInAction(() => {
				this.removeFailedSkeleton(taskIndex, selectedImage, imageIndex, tasksLength, deletedFlags, savedIndex);
			});
		} else if (error.response) {
			runInAction(() => {
				this.removeFailedSkeleton(taskIndex, selectedImage, imageIndex, tasksLength, deletedFlags, savedIndex);
			});
		}

		this.imageToImageStore.showAPIError(error);
	}

	private async processGenerationTasks(
		original: boolean,
		process_style: boolean,
		tasks: Task[],
		selectedImage: PlaygroundImage,
		config: ImageToImageConfigType,
		existingVariations: number,
		savedIndex: number,
		imageIndex: number,
		isFromUpload: boolean,
		deletedFlags: boolean[],
		selectedImages: PlaygroundImage[],
		resultImages: PlaygroundImage[],
	): Promise<void> {
		if (original) {
			const res = await this.runGenerateAPI(selectedImage, config, existingVariations, {
				original: true,
				tasks: [],
				process_style: false,
			});
			this.updateStoreWithResults(
				[{ results: res.results, original_url: res.original_url }],
				selectedImage,
				config,
				existingVariations,
				savedIndex,
				imageIndex,
				isFromUpload,
				resultImages,
			);
		} else if (process_style) {
			const res = await this.runGenerateAPI(selectedImage, config, existingVariations, {
				original: false,
				tasks: [],
				process_style: true,
			});
			this.updateStoreWithResults(
				[{ results: res.results, original_url: res.original_url }],
				selectedImage,
				config,
				existingVariations,
				savedIndex,
				imageIndex,
				isFromUpload,
				resultImages,
			);
		} else if (tasks?.length > 0) {
			await Promise.all(
				tasks.map(async (task: Task, taskIndex: number) => {
					try {
						console.log("currently running task: ", task);
						const res = await this.runGenerateAPI(selectedImage, config, existingVariations, {
							original: false,
							tasks: [task],
							process_style: false,
						});

						this.updateStoreWithResults(
							[
								{
									results: res.results,
									original_url: res.original_url,
									task,
								},
							],
							selectedImage,
							config,
							existingVariations,
							savedIndex,
							imageIndex,
							isFromUpload,
							resultImages,
						);
					} catch (error: any) {
						// Handle 420 error separately with fake results
						if (error.response && error.response.status === 420) {
							const errorData = error.response.data[0].error;
							const fakeResults: Array<{ results: any[]; original_url: string }> = [
								{
									results: [
										{
											id: "error-" + taskIndex,
											url: "",
											error: errorData,
										},
									],
									original_url: "",
								},
							];
							this.updateStoreWithResults(
								fakeResults,
								selectedImage,
								config,
								existingVariations,
								savedIndex,
								imageIndex,
								isFromUpload,
								resultImages,
							);
						} else {
							this.handleTaskError(
								error,
								taskIndex,
								selectedImage,
								selectedImages,
								imageIndex,
								tasks.length,
								deletedFlags,
								savedIndex,
							);
						}
					}
				}),
			);
		}
	}

	private async processSingleImageGeneration(
		selectedImage: PlaygroundImage,
		imageIndex: number,
		config: ImageToImageConfigType,
		savedIndex: number,
		isFromUpload: boolean,
		deletedFlags: boolean[],
		selectedImages: PlaygroundImage[],
		resultImages: PlaygroundImage[],
		featureString: string,
		isBulkMode: boolean,
		skipTracking?: boolean,
	): Promise<void> {
		try {
			const existingVariations = this.getExistingVariationsCount(selectedImage);
			const formData = this.createPrepareRequestFormData(selectedImage, config, existingVariations);
			const { original, process_style, tasks } = await this.prepareGenerationTasks(formData);

			await this.processGenerationTasks(
				original,
				process_style,
				tasks,
				selectedImage,
				config,
				existingVariations,
				savedIndex,
				imageIndex,
				isFromUpload,
				deletedFlags,
				selectedImages,
				resultImages,
			);
		} catch (e: any) {
			if (!skipTracking) {
				postHogService.trackEvent(
					"image_editing_failed",
					{
						feature: featureString,
						error: e.message || e.toString(),
						mode: isBulkMode ? ImageEditingViewMode.GALLERY : ImageEditingViewMode.SINGLE,
						image_count: selectedImages.length,
					},
					{ send_instantly: true },
				);
			}

			const { playgroundStore } = this.rootStore;
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
			});
			throw e;
		}
	}

	private async trackGenerationCompleted(
		featureString: string,
		isBulkMode: boolean,
		imageCount: number,
		resultsCount: number,
		startTime: number,
		skipTracking?: boolean,
	): Promise<void> {
		if (!skipTracking) {
			const processingTime = Date.now() - startTime;
			postHogService.trackEvent(
				POSTHOG_EVENTS.IMAGE_EDITING_COMPLETED,
				{
					feature: featureString,
					processing_time: processingTime,
					mode: isBulkMode ? ImageEditingViewMode.GALLERY : ImageEditingViewMode.SINGLE,
					image_count: imageCount,
					results_count: resultsCount,
				},
				{ send_instantly: true },
			);
		}
	}

	private sendPostGenerationMessages(
		resultImages: PlaygroundImage[],
		isFromUpload: boolean,
		isBulkMode: boolean,
	): void {
		if (isFromUpload) {
			iframeStore.sendUploadPostMessage(resultImages);
		} else {
			const configWithOriginalImage = {
				...this.imageToImageStore.config,
				original_image: this.lastSelectedImages[0] || this.imageToImageStore.config.original_image,
			};
			iframeStore.sendGeneratePostMessage(configWithOriginalImage, resultImages, isBulkMode);
		}
	}

	generateImageToImage = async (
		images?: PlaygroundImage[],
		config: ImageToImageConfigType = { ...this.imageToImageStore.config },
		isFromUpload?: boolean,
		numResults?: number,
		skipTracking?: boolean,
	): Promise<void> => {
		console.log("(generateImageToImage) generating image to image");

		// Initialize generation context
		const { selectedImages, startTime, isBulkMode, featureString } = this.initializeGenerationContext(
			images,
			config,
		);

		if (!selectedImages.length) return;

		// Track generation started
		await this.trackGenerationStarted(featureString, isBulkMode, selectedImages.length, skipTracking);

		const { playgroundStore } = this.rootStore;
		const savedIndex = isFromUpload
			? playgroundStore.playgroundResults.length - 1
			: playgroundStore.playgroundResults.length;

		// Update UI state
		runInAction(() => {
			this.isGeneratingImages = true;
		});

		// Create loading skeletons for non-upload flows
		if (!isFromUpload) {
			this.createLoadingSkeletons(config, selectedImages, numResults, false);
		}

		let resultImages: PlaygroundImage[] = [];
		playgroundStore.handleSelectSingleImage(null);

		// In bulk, if we generate results for multiple selected images, if one of the selected images
		// violate content moderation input, we don't want to stop the whole operation just delete the
		// results for the violated image
		const deletedFlags = Array(playgroundStore.playgroundResults[savedIndex]?.images.length || 0).fill(false);

		// Process each selected image
		try {
			for (let index = 0; index < selectedImages.length; index++) {
				const selectedImage = selectedImages[index];
				try {
					await this.processSingleImageGeneration(
						selectedImage,
						index,
						config,
						savedIndex,
						isFromUpload || false,
						deletedFlags,
						selectedImages,
						resultImages,
						featureString,
						isBulkMode,
						skipTracking,
					);
				} catch (ex: any) {
					// Currently there is no tasks generated, so tasksLength is 1
					const tasksLength = 1;
					this.handleTaskError(
						ex,
						index,
						selectedImage,
						selectedImages,
						index,
						tasksLength,
						deletedFlags,
						savedIndex,
					);
				}
			}
		} finally {
			runInAction(() => {
				this.isGeneratingImages = false;
			});
		}

		// Cleanup empty results
		this.cleanupEmptyResults(savedIndex);

		// Track successful completion
		await this.trackGenerationCompleted(
			featureString,
			isBulkMode,
			selectedImages.length,
			resultImages.length,
			startTime,
			skipTracking,
		);

		// Increment global generation count (for engagement popup logic)
		if (!isFromUpload) {
			this.rootStore.playgroundUserToOrgConversionStore.incrementGenerationCount();
		}

		// Send post messages
		this.sendPostGenerationMessages(resultImages, isFromUpload || false, isBulkMode);
	};

	private cleanupEmptyResults(savedIndex: number): void {
		const { playgroundStore } = this.rootStore;
		if (playgroundStore.playgroundResults[savedIndex]?.images.length === 0) {
			playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
				(_, index) => index !== savedIndex,
			);
		}
	}

	abortImageGeneration = () => {
		if (this.abortController) {
			this.abortController.abort();
			this.rootStore.playgroundStore.removeNotLoadingImages();

			// Re-select the images that were previously selected before the generation
			const lastSelectedImages = this.lastSelectedImages;
			if (lastSelectedImages.length > 0) {
				this.rootStore.playgroundStore.selectImages(lastSelectedImages);
			}

			// Reset playGroundLoadedImages on stop Generation
			this.rootStore.playgroundStore.clearPlayGroundLoadedImagesArray();
		}
	};

	private createLoadingSkeletons(
		config: ImageToImageConfigType,
		selectedImages: PlaygroundImage[],
		numResults: number | undefined,
		isFromUpload: boolean,
	): number {
		const { playgroundStore } = this.rootStore;
		const playground_result_id = uuidv4();
		const resultsSkeletons: PlaygroundResult = {
			id: playground_result_id,
			config: { ...config },
			type: playgroundStore.selectedConfig,
			images: Array.from({
				length:
					isFoxApps() || isFromUpload
						? selectedImages.length * config.num_results
						: (numResults ?? this.imageToImageStore.getGeneratedImagesCount),
			}).map((_) => ({
				id: uuidv4(),
				url: "",
				loading: true,
				playground_result_id: undefined,
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		return playgroundStore.playgroundResults.length - 1;
	}
}
